import { MutationFunction, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { DocumentType, gql } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import { assert, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";

const mutation = gql(/* GraphQL */ `
  mutation PatchBrandMetadata($brandId: UUID!, $metadataPatch: JSON!) {
    patchBrandMetadata(
      input: { brandId: $brandId, metadataPatch: $metadataPatch }
    ) {
      brand {
        id
        metadata
      }
    }
  }
`);

type PatchBrandMetadataMutationFunction = MutationFunction<
  DocumentType<typeof mutation>,
  DocumentVariablesType<typeof mutation>
>;

type PatchBrandMetadata = (
  brandId: UUID,
  metadata: GraphQLJSON,
) => ReturnType<PatchBrandMetadataMutationFunction>;

type PatchBrandMetadataForBrand = (
  metadata: GraphQLJSON,
) => ReturnType<PatchBrandMetadataMutationFunction>;

type Options = {
  isAdmin?: boolean;
};

export default function usePatchBrandMetadata({
  isAdmin,
}: Options = {}): PatchBrandMetadata {
  const [context, isContextReady] = useAdminOrBrandContext(isAdmin);
  const [patchBrandMetadata] = useMutation<
    DocumentType<typeof mutation>,
    DocumentVariablesType<typeof mutation>
  >(mutation, { context });

  return useCallback(
    (brandId: UUID, metadata: GraphQLJSON) => {
      assert(isContextReady, "Brand metadata patch context is not ready.");
      return patchBrandMetadata({
        variables: { brandId, metadataPatch: metadata },
      });
    },
    [isContextReady, patchBrandMetadata],
  );
}

export function usePatchBrandMetadataForBrand(
  brandId: UUID | null | undefined,
  options?: Options,
): PatchBrandMetadataForBrand {
  const patchBrandMetadata = usePatchBrandMetadata(options);

  return useCallback(
    (metadata: GraphQLJSON) => {
      assertPresence(brandId, "Brand ID is required to patch brand metadata.");
      return patchBrandMetadata(brandId, metadata);
    },
    [patchBrandMetadata, brandId],
  );
}

export function usePatchCurrentBrandMetadata() {
  const brandId = useCurrentBrandId();
  return usePatchBrandMetadataForBrand(brandId);
}
